/**
 * Enhanced Bathymetry data layer. Uses BathymetryManager for Cesium terrain/lighting; checkbox syncs with MapController.
 * Syncs from layout so layer shows when provided in initialLayers even before tab is opened.
 */
import React, { useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useDataLayer } from '../hooks/useDataLayer';
import { useMap } from '../MapContext';
import BathymetryManager from '../BathymetryManager/BathymetryManager';

export default function EnhancedBathymetryDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { slug, isReady, isActive, setActive, mapController } = useDataLayer(props);
  const { viewer } = useMap();
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady()) return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Enhanced Bathymetry</span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '12px',
          fontStyle: 'italic',
        }}
      >
        Detailed depth visualization with 25ft contour lines and gradient
        coloring
      </div>
      <div
        aria-label="Water depth legend in feet"
        style={{ marginBottom: '14px', color: '#6b7280' }}
      >
        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '5px' }}>
          Water Depth (Feet)
        </div>
        <div
          style={{
            width: '100%',
            height: '18px',
            background: `linear-gradient(to right,
              #8B7355 0%, #b59670 5%, #d9bb98 10%, #f0d08c 15%, #DEB887 20%,
              #F0E68C 25%, #FFFF99 30%, #FFFF66 35%, #FFFF33 40%, #CCFF33 42%,
              #99FF33 44%, #66FF33 46%, #33FF33 48%, #33FF66 50%, #33FF99 52%,
              #33FFCC 54%, #33FFFF 56%, #33CCFF 58%, #3399FF 60%, #3366FF 62%,
              #3333FF 64%, #6633FF 66%, #9933FF 68%, #CC33FF 70%, #FF33FF 72%,
              #FF3366 75%, #FF3333 80%, #CC0000 90%, #330000 100%)`,
            border: '1px solid rgba(0, 0, 0, 0.18)',
            borderRadius: '3px',
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '9px',
            marginTop: '3px',
          }}
        >
          <span>1000</span>
          <span>0</span>
          <span>-1600</span>
          <span>-3300</span>
          <span>-13000</span>
        </div>
        <div style={{ fontSize: '10px', marginTop: '5px' }}>
          Black lines = 25 ft depth contours
        </div>
      </div>
      {viewer && (
        <BathymetryManager
          cesiumViewer={viewer}
          isVisible={isActive}
        />
      )}
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setActive(e.target.checked)
            }
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Enhanced Bathymetry</span>
        </label>
      </div>
    </div>
  );
}
